import { LoaderCircle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import type { PicklistDto } from '../../types';
import { itemOverRelease, qty } from '../../utils/picklistProgress';
import { stillPosting } from './postRows';

interface SecondBatchConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  picklist: PicklistDto;
  /** What is picked and not yet in any batch (`aggregate().postable`). */
  postable: number;
  onGo: () => void;
}

/**
 * PT-5 deliverable 10 — "Post another batch?" before the dialog opens while a batch is still posting. A VIEWPORT
 * dialog (it sits outside the content column in the prototype, L1781), in the prototype's words (O-20 slots 17-24).
 *
 * The prototype's failed-batch line (slot 23) is not rendered. It sorts failed batches into "retrying itself" and
 * "rejected — nothing was issued", and neither maps onto the outbox honestly: a batch that is retrying itself is
 * still Pending here, so it is already counted above as posting; a Failed batch never retries on its own, it waits
 * for a person; and "nothing was issued" holds only when the batch's `postReach` proves it. The posting strip
 * above the table already shows each failed batch with its own recovery.
 */
export function SecondBatchConfirm({ open, onOpenChange, picklist, postable, onGo }: SecondBatchConfirmProps) {
  const open_ = stillPosting(picklist);
  const contents = new Map((picklist.postingBatchContents ?? []).map((c) => [c.postingBatchId, c] as const));
  const openUnits = open_.reduce((a, b) => a + (contents.get(b.id)?.totalQuantity ?? 0), 0);
  // ISS-R-117 — planned LINES over their MRS (the sentence says lines; it used to count items). Their excess is
  // left out; what fits the headroom is in `postable`.
  const blocked = (picklist.itemGroups ?? []).flatMap((g) => g.plannedLines ?? []).filter((l) => itemOverRelease(l) > 0).length;

  const headline = open_.length === 1
    ? `Batch #${open_[0].id} is still posting.`
    : `${open_.length} batches are still posting.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        overlayClassName="bg-black/45"
        className="max-w-[470px] gap-0 overflow-hidden rounded-xl p-0 sm:rounded-xl"
      >
        <div className="flex gap-[11px] px-5 pt-[18px]">
          <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] bg-info/[0.12] text-info" aria-hidden>
            <LoaderCircle className="h-[17px] w-[17px] animate-spin motion-reduce:animate-none" />
          </span>
          <div className="min-w-0">
            <DialogTitle className="text-[15.5px] font-bold tracking-[-0.01em]">{headline}</DialogTitle>
            <DialogDescription className="mt-[5px] text-[12.5px] leading-[1.55]">
              This creates an additional batch — that is normal for a picklist picked in stages. It will not re-send
              anything already posting.
            </DialogDescription>
          </div>
        </div>

        <div className="mx-5 my-3.5 flex flex-col gap-2 rounded-[9px] border border-border bg-muted/35 px-[13px] py-[11px]">
          <div className="flex items-center gap-[9px]">
            <span className="h-2 w-2 shrink-0 rounded-full bg-info" aria-hidden />
            <span className="text-xs tabular-nums">
              {qty(openUnits)} units posting in {open_.length} {open_.length === 1 ? 'open batch' : 'open batches'}
            </span>
          </div>
          <div className="flex flex-wrap gap-[5px] pl-[17px]">
            {open_.map((b) => {
              const c = contents.get(b.id);
              return (
                <span key={b.id} className="inline-flex h-[21px] items-center gap-1.5 rounded-md border border-border bg-card px-2 text-[11px] font-medium">
                  <span className="font-mono font-semibold">#{b.id}</span>
                  {c?.contentsResolved && (
                    <span className="tabular-nums text-muted-foreground">
                      {qty(c.totalQuantity)} units · {c.lines.length} {c.lines.length === 1 ? 'line' : 'lines'}
                    </span>
                  )}
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-[9px]">
            <span className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/50" aria-hidden />
            <span className="text-xs tabular-nums">{qty(postable)} units ready and clear to post</span>
          </div>
          {blocked > 0 && (
            <div className="flex items-start gap-[9px]">
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-destructive" aria-hidden />
              <span className="text-xs leading-[1.45] text-muted-foreground">
                {blocked} {blocked === 1 ? 'line is' : 'lines are'} over what its MRS still allows to be issued. Only
                what fits is counted above; the rest is left out of this batch.
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border bg-muted/25 px-5 py-3">
          <Button variant="outline" className="h-[34px] text-[12.5px]" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button className="h-[34px] gap-1.5 text-[12.5px] font-semibold" disabled={postable <= 0} onClick={onGo}>
            <Send className="h-[13px] w-[13px]" aria-hidden />
            Post {qty(postable)} units as a new batch
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
